import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import CtaSection from "@/components/CtaSection";
import { TREATMENTS } from "@/data/content";

const WIDE = {
  0: "/images/surgery-2.jpg",
  3: "/images/patient-care.jpg",
  4: "/images/surgery-team.jpg",
  7: "/images/support-hands.jpg",
};

export default function Treatments() {
  return (
    <>
      <SEO
        title="Treatment Options in India: Cardiology, Oncology & More | GetWellSoon"
        description="Explore treatment areas coordinated by GetWellSoon — cardiology, orthopedics, oncology, neurology, spine care, kidney care, liver care, and IVF — with leading hospitals in India."
      />

      <section className="bg-pearl" data-testid="treatments-hero">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Chapter index="01" label="Treatment areas" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              delay={0.1}
              className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:col-span-8 lg:text-6xl"
              lines={[
                "Specialties we",
                <span key="l2">
                  <span className="italic text-brand">coordinate</span> for you.
                </span>,
              ]}
            />
            <FadeIn delay={0.4} className="lg:col-span-4">
              <p className="text-base leading-relaxed text-muted-foreground">
                We coordinate across eight specialty areas with leading
                hospitals in India. Every referral begins with your actual
                medical reports — never a generic package.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="treatments-grid">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TREATMENTS.map((t, i) => {
              const wide = WIDE[i];
              return (
                <FadeIn
                  key={t.slug}
                  delay={(i % 4) * 0.06}
                  className={`group flex flex-col overflow-hidden rounded-3xl border border-border bg-white transition-colors duration-300 hover:border-brand/40 ${
                    wide ? "lg:col-span-2" : ""
                  }`}
                  data-testid={`treatment-card-${t.slug}`}
                >
                  {wide && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={wide}
                        alt={`${t.name} care environment`}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-8">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <t.icon className="h-5 w-5" />
                    </span>
                    <h2 className="mt-6 font-serif text-2xl tracking-tight text-ink">
                      {t.name}
                    </h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {t.description}
                    </p>
                    <Link
                      to="/contact"
                      data-testid={`treatment-inquire-${t.slug}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand transition-colors hover:text-brand-dark"
                    >
                      Discuss this treatment
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          <FadeIn
            className="mt-14 rounded-2xl border-l-4 border-sage bg-sage-light/60 p-6 md:p-8"
            data-testid="treatments-note"
          >
            <p className="text-sm leading-relaxed text-ink/80 md:text-base">
              <span className="font-bold">Please note:</span> Treatment
              availability and suitability are determined by the treating
              hospital and physicians. GetWellSoon does not provide medical
              advice, recommend specific procedures, or influence clinical
              decisions.
            </p>
          </FadeIn>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
